import { useState } from "react";
import { BellRing, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { createAlert } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const KEY = "correl_draw_alerts";
const saved = () => { try { return JSON.parse(sessionStorage.getItem(KEY) || "{}"); } catch { return {}; } };

function validate(email, score, max, latestCutoff) {
  const n = Number(score);
  if (!EMAIL_RE.test(email)) return { error: "Please enter a valid email address" };
  if (!Number.isInteger(n) || n < 0 || n > max) return { error: `Enter a whole score between 0 and ${max}` };
  if (latestCutoff != null && n >= latestCutoff) return { info: `Your score already meets the latest cutoff of ${latestCutoff} — no alert needed` };
  return { n };
}

function useDrawAlert(stream, max, latestCutoff) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(() => saved()[stream.id] || null);
  const submit = async (email, score) => {
    const v = validate(email, score, max, latestCutoff);
    if (v.error) return toast.error(v.error);
    if (v.info) return toast.info(v.info);
    setBusy(true);
    try {
      await createAlert({ email: email.trim(), streamId: stream.id, score: v.n });
      const rec = { email: email.trim().toLowerCase(), score: v.n };
      sessionStorage.setItem(KEY, JSON.stringify({ ...saved(), [stream.id]: rec }));
      setDone(rec);
      toast.success("Draw alert set — check your inbox for a confirmation");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set the alert. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return { busy, done, submit };
}

function AlertDone({ stream, done }) {
  return (
    <p className="mt-4 flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid={`draw-alert-done-${stream.id}`}>
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
      <span>Alert set — we'll email <strong>{done.email}</strong> when the cutoff drops to {done.score} or below.</span>
    </p>
  );
}

function AlertForm({ stream, max, busy, onSubmit, onClose }) {
  const [email, setEmail] = useState("");
  const [score, setScore] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(email, score); }} className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`draw-alert-form-${stream.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold text-slate-600">Email me when this cutoff drops to my score ({stream.scoring_model === "crs_based" ? "CRS" : `out of ${max}`}).</p>
        <button type="button" onClick={onClose} aria-label="Close" data-testid={`draw-alert-close-${stream.id}`} className="flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input type="number" min={0} max={max} value={score} onChange={(e) => setScore(e.target.value)} placeholder="Your score" data-testid={`draw-alert-score-${stream.id}`}
          className="w-full min-h-[44px] rounded-md border border-slate-300 px-3 py-2 text-sm sm:w-28" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid={`draw-alert-email-${stream.id}`}
          className="min-h-[44px] min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <button type="submit" disabled={busy} data-testid={`draw-alert-submit-${stream.id}`}
          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-md bg-[#C8102E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#A60C24] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Alert me
        </button>
      </div>
    </form>
  );
}

export function DrawPageAlert({ stream, latestCutoff }) {
  const [open, setOpen] = useState(false);
  const max = stream.scoring_model === "crs_based" ? 1200 : stream.max_points;
  const { busy, done, submit } = useDrawAlert(stream, max, latestCutoff);
  if (stream.scoring_model === "eligibility") return null;
  if (done) return <AlertDone stream={stream} done={done} />;
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`draw-alert-open-${stream.id}`}
        className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#C8102E] hover:text-[#C8102E]">
        <BellRing className="h-4 w-4" /> Set a draw alert
      </button>
    );
  }
  return <AlertForm stream={stream} max={max} busy={busy} onSubmit={submit} onClose={() => setOpen(false)} />;
}
